// src/App.js - With route protection and dark mode
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DialogProvider } from './contexts/DialogContext';
import { DarkModeProvider } from './pages/Settings'; // Added dark mode import
import ProtectedRoute, { UnauthorizedPage } from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import UserProfile from './pages/UserProfile';
import Settings from './pages/Settings';
import Dashboard from './pages/Dashboard';
import FlightLog from './pages/FlightLog';
import FlightForm from './pages/FlightForm';
import Aircraft from './pages/Aircraft';
import AddAircraft from './pages/AddAircraft';
import EditAircraft from './pages/EditAircraft';
import Crew from './pages/Crew';
import AddCrew from './pages/AddCrew';
import EditCrew from './pages/EditCrew';
import Statistics from './pages/Statistics';
import FlightSearchApp from './components/FlightSearch';
import UserManagement from './pages/admin/UserManagement';
import CrewAccessImport from './pages/CrewAccessImport';
import DataManager from './pages/DataManager';
import EFBPanel from './components/EFBPanel';

function App() {
  return (
    <DarkModeProvider> {/* Added DarkModeProvider wrapper */}
      <AuthProvider>
        <DialogProvider>
        <Router>
          <div className="App min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200"> {/* Added dark mode classes */}
            <Navbar />
            <main className="pt-16">
              <Routes>
                {/* Public routes - no authentication required */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                
                {/* Protected routes - require authentication */}

                <Route path="/far117" element={
                  <ProtectedRoute>
                    <EFBPanel />
                  </ProtectedRoute>
                } />

                 <Route path="/crewaccess" element={
                  <ProtectedRoute>
                    <CrewAccessImport />
                  </ProtectedRoute>
                } />

                <Route path="/data" element={
                  <ProtectedRoute>
                    <DataManager/>
                  </ProtectedRoute>
                } />

                <Route path="/" element={<HomeRoute />} />
                
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                } />
                
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                
                <Route path="/flights" element={
                  <ProtectedRoute>
                    <FlightLog />
                  </ProtectedRoute>
                } />
                
                <Route path="/add-flight" element={
                  <ProtectedRoute>
                    <FlightForm key="add" />
                  </ProtectedRoute>
                } />
                
                <Route path="/edit-flight/:id" element={
                  <ProtectedRoute>
                    <FlightForm key="edit" />
                  </ProtectedRoute>
                } />
                
                <Route path="/aircraft" element={
                  <ProtectedRoute>
                    <Aircraft />
                  </ProtectedRoute>
                } />
                
                <Route path="/add-aircraft" element={
                  <ProtectedRoute>
                    <AddAircraft />
                  </ProtectedRoute>
                } />
                
                <Route path="/edit-aircraft/:id" element={
                  <ProtectedRoute>
                    <EditAircraft />
                  </ProtectedRoute>
                } />
                
                <Route path="/crew" element={
                  <ProtectedRoute>
                    <Crew />
                  </ProtectedRoute>
                } />
                
                <Route path="/add-crew" element={
                  <ProtectedRoute>
                    <AddCrew />
                  </ProtectedRoute>
                } />
                
                <Route path="/edit-crew/:id" element={
                  <ProtectedRoute>
                    <EditCrew />
                  </ProtectedRoute>
                } />
                
                <Route path="/statistics" element={
                  <ProtectedRoute>
                    <Statistics />
                  </ProtectedRoute>
                } />
                
                <Route path="/search" element={
                  <ProtectedRoute>
                    <FlightSearchApp />
                  </ProtectedRoute>
                } />

              {/* Admin only routes */}
                <Route path="/admin/users" element={
                  <ProtectedRoute requireAdmin={true}>
                    <UserManagement />
                  </ProtectedRoute>
                } />
              </Routes>
            </main>
            
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                },
                className: 'dark:!bg-gray-800 dark:!text-white dark:!border-gray-600', // Added dark mode support for toasts
                success: {
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </Router>
        </DialogProvider>
      </AuthProvider>
    </DarkModeProvider>
  );
}

// "/" is the public home page until you sign in, then the Dashboard
const HomeRoute = () => {
  const { loading, isAuthenticated } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return isAuthenticated() ? <Dashboard /> : <Home />;
};

// Keep your existing AboutPage and ContactPage components here...
const AboutPage = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors duration-200"> {/* Added dark mode classes */}
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/50 p-8 transition-colors duration-200"> {/* Added dark mode classes */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">About Compflightlog</h1> {/* Added dark mode classes */}
        <p className="text-gray-600 dark:text-gray-400"> {/* Added dark mode classes */}
          Compflightlog is a comprehensive flight tracking application designed for pilots of all experience levels.
        </p>
      </div>
    </div>
  </div>
);

const ContactPage = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors duration-200"> {/* Added dark mode classes */}
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/50 p-8 transition-colors duration-200"> {/* Added dark mode classes */}
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Contact Us</h1> {/* Added dark mode classes */}
        <p className="text-gray-600 dark:text-gray-400">
          Get in touch with our support team at{' '}
          <a href="mailto:support@aerosynapsesystems.com" className="text-blue-600 hover:text-blue-700 dark:text-blue-300">support@aerosynapsesystems.com</a>.
        </p> {/* Added dark mode classes */}
      </div>
    </div>
  </div>
);

export default App;